import { createAsyncThunk, createSlice, type Reducer } from "@reduxjs/toolkit";
import { fetchJobList, type JobListItem, type JobListFilters } from "./jobAPI";

export interface JobDisplayData {
  id: number;
  position: string;
  location: string;
  daysOpen: number | null;
  department: string;
  // avatar can be a file ID (number), a URL (string), or null
  hiringManager: { name: string; avatar: number | string | null; avatarUrl: string | null };
  inMatch: number;
  topMatch: number;
  /** Display status: Open, Closed, or On-hold */
  status: "Open" | "Closed" | "On-hold";
}

export interface JobsState {
  jobs: JobDisplayData[];
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  pagination: {
    count: number;
    totalPages: number;
    currentPage: number;
  } | null;
}

type JobsRootState = { jobs: JobsState };

export const selectJobsList = (state: JobsRootState) => state.jobs.jobs;
export const selectJobsStatus = (state: JobsRootState) => state.jobs.status;
export const selectJobsError = (state: JobsRootState) => state.jobs.error;
export const selectJobsPagination = (state: JobsRootState) => state.jobs.pagination;

const initialState: JobsState = {
  jobs: [],
  status: "idle",
  error: null,
  pagination: null,
};

const transformJobData = (job: JobListItem): JobDisplayData => {
  const daysOpen =
    job.days_open != null ? Math.max(0, job.days_open) : null;

  // Normalize API status for display (Close → Closed)
  let status: "Open" | "Closed" | "On-hold" = "Open";
  const apiStatus = (job.status ?? "").trim().toLowerCase();
  if (apiStatus === "close" || apiStatus === "closed") {
    status = "Closed";
  } else if (job.status === "On-hold") {
    status = "On-hold";
  } else if (job.status === "Open") {
    status = "Open";
  }

  // Get hiring manager name - use direct name field if available, otherwise construct from first/middle/last
  const hiringManagerName = job.hiring_manager
    ? job.hiring_manager.name || 
      `${job.hiring_manager.first_name || ""} ${job.hiring_manager.middle_name || ""} ${job.hiring_manager.last_name || ""}`.trim() || 
      "N/A"
    : "N/A";

  // Location format: "country, city"
  const location = job.country?.name && job.city?.name
    ? `${job.country.name}, ${job.city.name}`
    : job.country?.name || job.city?.name || "N/A";

  return {
    id: job.id,
    position: job.job_title || "N/A",
    location,
    daysOpen,
    department: job.department?.name || "N/A",
    hiringManager: {
      name: hiringManagerName,
      avatar: job.hiring_manager?.profile_picture ?? null,
      avatarUrl: job.hiring_manager?.profile_picture_url ?? null,
    },
    inMatch: job.in_match ?? job.new_matches ?? 0,
    topMatch: job.top_match || 0,
    status,
  };
};

export const fetchJobs = createAsyncThunk<
  { jobs: JobDisplayData[]; pagination: { count: number; totalPages: number; currentPage: number } | null },
  { accessToken: string; filters?: JobListFilters },
  { rejectValue: { message: string } }
>("jobs/fetchJobs", async ({ accessToken, filters }, { rejectWithValue }) => {
  try {
    const response = await fetchJobList(accessToken, filters);
    if (response.error) {
      return rejectWithValue({ message: response.error });
    }
    const jobsData = response.data || [];
    const transformedJobs = jobsData.map(transformJobData);
    
    const pagination = response.pagination ? {
      count: response.pagination.count,
      totalPages: response.pagination.total_pages,
      currentPage: response.pagination.current_page,
    } : null;
    
    return { jobs: transformedJobs, pagination };
  } catch (error) {
    if (error instanceof Error) {
      return rejectWithValue({ message: error.message });
    }
    return rejectWithValue({ message: "Unable to fetch jobs. Please try again." });
  }
});

const jobsSlice = createSlice({
  name: "jobs",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchJobs.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchJobs.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.jobs = action.payload.jobs;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(fetchJobs.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message || "Failed to fetch jobs.";
      });
  },
});

const jobsReducer: Reducer<JobsState> = jobsSlice.reducer;

export default jobsReducer;

